import React, { useContext, useMemo } from 'react';
import { style } from 'typestyle';
import * as styles from '../../modals/modal.styles';
import DayPartDepartmentGroup from './day-part-department-group.component';
import DayPartRoomGroup from './day-part-room-group.component';
import DayPartFloorPlanSection from './day-part-floor-plan-section.component';
import Column from '../../../../core/components/column/column.component';
import { MainAxisAlignment } from '../../../../core/components/flex/flex.component';
import Row from '../../../../core/components/row/row.component';
import { IsMobileContext } from '../../../../core/context/is-mobile';
import type Department from '../../../offices/models/department/department';
import type Office from '../../../offices/models/office';
import type RoomObsolete from '../../../offices/models/room/room-obsolete';
import { type DayPart } from '../../models/daypart-entry';
import type FloorPlan from '../../../dashboard/models/floor-plan';
import { retiredWorkplaceIdsFor } from '../../utils/retired-workplaces';
import roomsForDepartment from '../../utils/rooms-for-department';

interface Props {
  readonly dayPart: DayPart;
  readonly title: string;
  readonly office: Office;
  readonly rooms: RoomObsolete[] | undefined;
  readonly selectedRoom: RoomObsolete | undefined;
  readonly selectedDepartment: Department | undefined;
  readonly onRoomSelectionChanged: (room: RoomObsolete) => void;
  readonly onDepartmentSelectionChanged: (department: Department) => void;
  // Floorplan props (optional — only used when office has floorPlanEnabled)
  readonly officeId?: string;
  readonly selectedDates?: Date[];
  readonly floorPlans?: FloorPlan[];
  readonly selectedWorkplaceId?: string;
  readonly onWorkplaceSelected?: (workplaceId: string) => void;
}

const DayPartGroup = ({
  dayPart,
  title,
  office,
  rooms,
  selectedRoom,
  selectedDepartment,
  onRoomSelectionChanged,
  onDepartmentSelectionChanged,
  officeId,
  selectedDates,
  floorPlans,
  selectedWorkplaceId,
  onWorkplaceSelected,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);

  const roomWorkplaceIds = useMemo(() => {
    if (!selectedRoom?.workplaces) return [];
    return selectedRoom.workplaces
      .map((w) => w.id)
      .filter((id): id is string => id != null);
  }, [selectedRoom]);

  const showFloorPlan =
    office.floorPlanEnabled === true &&
    selectedRoom?.isReservableThroughFloorPlan === true &&
    floorPlans != null;

  const roomsForSelectedDepartment = (): RoomObsolete[] | undefined =>
    roomsForDepartment(rooms, office.departments, selectedDepartment);

  return (
    <Column>
      <span className={style(styles.columnHeader)}>{title}</span>
      {isMobile ? (
        <Column>
          {office.departments.length !== 0 && (
            <DayPartDepartmentGroup
              departments={office.departments}
              onDepartmentSelectionChanged={onDepartmentSelectionChanged}
              selectedDepartment={selectedDepartment}
            />
          )}
          <DayPartRoomGroup
            dayPart={dayPart}
            rooms={roomsForSelectedDepartment()}
            onRoomSelectionChanged={onRoomSelectionChanged}
            selectedRoom={selectedRoom}
          />
        </Column>
      ) : (
        <Row itemSpacing={20} mainAxisAlignment={MainAxisAlignment.Stretch}>
          {office.departments.length !== 0 && (
            <DayPartDepartmentGroup
              departments={office.departments}
              onDepartmentSelectionChanged={onDepartmentSelectionChanged}
              selectedDepartment={selectedDepartment}
            />
          )}
          <DayPartRoomGroup
            dayPart={dayPart}
            rooms={roomsForSelectedDepartment()}
            onRoomSelectionChanged={onRoomSelectionChanged}
            selectedRoom={selectedRoom}
          />
        </Row>
      )}
      {showFloorPlan && officeId != null && selectedDates != null && selectedDates.length > 0 && onWorkplaceSelected != null && (
        <DayPartFloorPlanSection
          dayPart={dayPart}
          officeId={officeId}
          selectedDates={selectedDates}
          floorPlans={floorPlans}
          roomWorkplaceIds={roomWorkplaceIds}
          retiredWorkplaceIds={retiredWorkplaceIdsFor(rooms, selectedDates)}
          selectedWorkplaceId={selectedWorkplaceId}
          onWorkplaceSelected={onWorkplaceSelected}
        />
      )}
    </Column>
  );
};

export default DayPartGroup;
